import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar, Badge, Typography } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { SegmentedAppointment } from '../segments';
import { ScheduleException } from '../../../services/scheduleApi';
import { expandExceptionsClient } from '../exceptionSegments';

const { Text } = Typography;

export interface MonthViewProps {
  anchor: Dayjs;
  appts: SegmentedAppointment[];
  exceptions: ScheduleException[];
  onSelectDate: (d: Dayjs) => void;
}

const MAX_CHIPS = 3;

// Mirrors AppointmentCalendar's status→badge mapping so this tab's month grid
// reads consistently with the rest of the app.
function statusBadge(status: string): 'success' | 'processing' | 'default' | 'error' | 'warning' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'processing';
    case 'confirmed':
    case 'scheduled':
      return 'default';
    case 'cancelled':
      return 'error';
    case 'no_show':
      return 'warning';
    default:
      return 'default';
  }
}

function displayTitle(appt: SegmentedAppointment): string {
  return (appt as any).customer_name || appt.customer?.name || appt.title || 'Appointment';
}

// Expand recurrence over the visible month, then bucket by the days each
// block actually covers (a block can span multiple days). Building a plain
// day→count map — rather than reusing bucketExceptionsByDate's lane-pixel
// math — because Month view has no open/close track to position against, it
// only needs "does this day have any block, and how many".
function exceptionsByDate(exceptions: ScheduleException[], monthStart: Dayjs, monthEnd: Dayjs): Map<string, number> {
  const rangeStart = monthStart.format('YYYY-MM-DD');
  const rangeEnd = monthEnd.format('YYYY-MM-DD');
  const expanded = expandExceptionsClient(exceptions, rangeStart, rangeEnd);
  const counts = new Map<string, number>();

  for (const ex of expanded) {
    const s = dayjs(ex.start_datetime);
    const e = dayjs(ex.end_datetime);
    if (!s.isValid() || !e.isValid()) continue;
    const lastDay = e.startOf('day');
    for (let cur = s.startOf('day'); !cur.isAfter(lastDay, 'day'); cur = cur.add(1, 'day')) {
      if (cur.isBefore(monthStart, 'day') || cur.isAfter(monthEnd, 'day')) continue;
      const dateStr = cur.format('YYYY-MM-DD');
      counts.set(dateStr, (counts.get(dateStr) || 0) + 1);
    }
  }
  return counts;
}

// Lifted from Appointments/AppointmentCalendar.tsx's month cellRender — same
// chip-per-appointment-per-day rendering, adapted to key off the shared
// SegmentedAppointment shape (segments carry the date(s) an appt occupies)
// instead of a single start_time, and to call back into the parent (this
// component is presentational; it doesn't fetch or own a modal).
export function MonthView({ anchor, appts, exceptions, onSelectDate }: MonthViewProps) {
  const apptsByDate = new Map<string, SegmentedAppointment[]>();
  appts.forEach((appt) => {
    const dates = Array.from(new Set(appt.segments.map((s) => s.date)));
    dates.forEach((d) => {
      const list = apptsByDate.get(d) || [];
      list.push(appt);
      apptsByDate.set(d, list);
    });
  });

  const exceptionCountsByDate = exceptionsByDate(exceptions, anchor.startOf('month'), anchor.endOf('month'));

  const cellRender = (current: Dayjs, info: { type: string; originNode: React.ReactNode }) => {
    if (info.type !== 'date') return info.originNode;

    const dateStr = current.format('YYYY-MM-DD');
    const dayAppts = apptsByDate.get(dateStr) || [];
    const exceptionCount = exceptionCountsByDate.get(dateStr) || 0;
    if (dayAppts.length === 0 && exceptionCount === 0) return null;

    return (
      <div data-testid={`month-cell-${dateStr}`} style={{ fontSize: 12 }}>
        {exceptionCount > 0 && (
          <div data-testid={`month-exception-${dateStr}`} title="Availability blocked">
            <Text
              type="secondary"
              style={{
                fontSize: 11,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <StopOutlined aria-hidden style={{ fontSize: 10 }} />
              {exceptionCount} off
            </Text>
          </div>
        )}
        {dayAppts.slice(0, MAX_CHIPS).map((appt) => (
          <div key={appt.id} data-testid="month-chip">
            <Badge
              status={statusBadge(appt.status)}
              text={
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    display: 'inline-block',
                  }}
                >
                  {dayjs(appt.start_time).format('h:mma')} {displayTitle(appt)}
                </span>
              }
            />
          </div>
        ))}
        {dayAppts.length > MAX_CHIPS && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            +{dayAppts.length - MAX_CHIPS} more
          </Text>
        )}
      </div>
    );
  };

  return (
    <div data-testid="month-view">
      {/* AntD's own Calendar header duplicates ScheduleControls' month
          nav (prev/next/today) — suppress it so only ScheduleControls
          drives navigation for this tab. */}
      <Calendar
        value={anchor}
        cellRender={cellRender}
        onSelect={(date) => onSelectDate(date)}
        headerRender={() => null}
      />
    </div>
  );
}

export default MonthView;
